import React, { useEffect, useState } from 'react';
import { Form, Formik, FieldArray } from 'formik';
import { capitalizeWords, customStyle } from '@custom/Utils/utils';
import { getIcon } from '@ohif/ui';
import * as Yup from 'yup';

const ConsultationForm = ({ editConsultation, initialValues, submitForm, edit, setEdit, modalities, radiologistNames, setShowConsultationForm, isSubmitting, existingRadiologists }) => {

    const [modRadioNames, setModRadioNames] = useState([]);

    const consultationSchema = Yup.object().shape({
        radiologistId: Yup.string().required('Radiologist is required'),
        consultationCharges: Yup.array().of(
            Yup.object().shape({
                modality: Yup.string().required('Modality is required'),
                charge: Yup.number()
                    .required('Charge is required')
                    .positive('Charge must be greater than 0')
            })
        )
    });

    useEffect(() => {
        const modList = !edit
            ? radiologistNames?.filter(
            radiologist => !existingRadiologists?.includes(radiologist._id)
        )
        : radiologistNames;
        setModRadioNames(modList);
    }, [existingRadiologists, radiologistNames])

    const getAvailableModalities = (values, currentIndex) => {
        const selectedModalities = values.consultationCharges
            .map((charge, index) => index !== currentIndex ? charge.modality : null)
            .filter(modality => modality !== null && modality !== '');

        return modalities.filter(modality => !selectedModalities.includes(modality));
    };

    return (
        <div className="details">
            <div className="form form--innersec">
                <div className="mb-3">
                    <button
                        className=''
                        onClick={() => setShowConsultationForm(false)}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {getIcon('icon-toolbar-back')}
                        Back
                    </button>
                </div>
                <section className="form__section">
                    <h1 className="form__section__title">
                        {editConsultation ? "Edit Consultation Charge" : "Add Consultation Charge"}
                    </h1>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={consultationSchema}
                        onSubmit={submitForm}
                        enableReinitialize={true}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            errors,
                            touched
                        }) => {
                            const isFormValid =
                                values.radiologistId &&
                                values.consultationCharges.length > 0 &&
                                values.consultationCharges.every(charge =>
                                    charge.modality && charge.charge && Number(charge.charge) > 0
                                );

                            return (
                                <Form onSubmit={handleSubmit}>
                                    <div className="formRow formRow__3">
                                        <div className="formCol mt-1">
                                            <div className="form__Field">
                                                <label className="form__label" htmlFor="radiologistName">
                                                    Radiologist Name<span className="required-asterisk">*</span>
                                                </label>
                                                <select
                                                    id="radiologistName"
                                                    name="radiologistName"
                                                    className="form__select form__select--sm"
                                                    onChange={(e) => {
                                                        const selectedRadiologist = modRadioNames?.find((r) => r._id === e.target.value);
                                                        setFieldValue('radiologistName', selectedRadiologist?.name || '');
                                                        setFieldValue('radiologistId', selectedRadiologist?._id || '');
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={values.radiologistId}
                                                    autoComplete="off"
                                                    style={customStyle.select}
                                                    disabled={edit}
                                                >
                                                    <option value="">-Select-</option>
                                                    {modRadioNames?.map((radiologist) => (
                                                        <option key={radiologist._id} value={radiologist._id}>
                                                            {capitalizeWords(radiologist?.name)}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.radiologistId && touched.radiologistId && (
                                                    <span className="text-red-500 text-sm font-medium">{errors.radiologistId}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <FieldArray name="consultationCharges">
                                        {({ push, remove }) => (
                                            <div>
                                                {values?.consultationCharges?.map((charge, index) => {
                                                    const availableModalities = getAvailableModalities(values, index);
                                                return (<div key={index} className="charge-field-group">
                                                        <div className="formRow formRow__2">
                                                            <div className="formCol">
                                                                <div className="form__Field">
                                                                    <label className="form__label">
                                                                        Modality
                                                                        {index === 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className="text-lg text-[#0F8491] ml-2"
                                                                                onClick={() => push({ modality: '', charge: '' })}
                                                                            >
                                                                                + Add More
                                                                            </button>
                                                                        )}
                                                                        {index > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className="text-lg text-red-600 ml-2"
                                                                                onClick={() => remove(index)}
                                                                            >
                                                                                - Remove
                                                                            </button>
                                                                        )}
                                                                    </label>
                                                                    <select
                                                                        className="form__select form__select--sm"
                                                                        name={`consultationCharges.${index}.modality`}
                                                                        value={charge.modality}
                                                                        style={customStyle.select}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    >
                                                                        <option value="">-Select-</option>
                                                                        {availableModalities?.map((modality) => (
                                                                            <option key={modality} value={modality}>
                                                                                {modality}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {errors.consultationCharges?.[index]?.modality && (
                                                                    <span className="text-red-500 text-sm font-medium">
                                                                            {errors.consultationCharges[index].modality}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="formCol">
                                                                <div className="form__Field">
                                                                    <label className="form__label">
                                                                        Consultation Charge
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form__input form__input--sm"
                                                                        name={`consultationCharges.${index}.charge`}
                                                                        placeholder="Charge"
                                                                        value={charge.charge}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            if (/^\d*\.?\d*$/.test(value) || value === '') {
                                                                                setFieldValue(`consultationCharges.${index}.charge`, value);
                                                                            }
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {errors.consultationCharges?.[index]?.charge && (
                                                                    <span className="text-red-500 text-sm font-medium">
                                                                            {errors.consultationCharges[index].charge}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                )}
                                            </div>
                                        )}
                                    </FieldArray>

                                    <div className="btn__group">
                                        <button
                                            className="btn btn__white"
                                            type="button"
                                            onClick={() => {
                                                setShowConsultationForm(false);
                                                setEdit(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="btn btn__primary"
                                            type="submit"
                                            disabled={!isFormValid || isSubmitting}
                                        >
                                            {edit ? "Update" : "Save"}
                                        </button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </section>
            </div>
        </div>
    );
};

export default ConsultationForm;
